/**
 * Trending Tab Component
 * Shows trending AI-generated posts
 */
import { Button } from "@/components/ui/button";
import {
  TrendingUp,
  Loader2,
  Sparkles,
} from "lucide-react";

export const TrendingTab = ({
  trendingPosts,
  loadingTrending,
  onUsePrompt,
}) => {
  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-gradient-to-r from-rose-500 to-pink-600">
              <TrendingUp className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-800 dark:text-white">Trending Now</h2>
              <p className="text-gray-500 text-sm">3 free AI-generated posts daily based on viral trends</p>
            </div>
          </div>
          <span className="px-3 py-1 rounded-full bg-green-100 text-green-700 text-sm font-medium">FREE Daily</span>
        </div>

        {/* Content */}
        {loadingTrending ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-pink-600" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {trendingPosts.map((post, idx) => (
              <TrendingCard 
                key={post.id} 
                post={post} 
                index={idx} 
                onUsePrompt={onUsePrompt}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const TrendingCard = ({ post, index, onUsePrompt }) => (
  <div className="bg-gradient-to-br from-slate-50 to-white border border-gray-200 rounded-xl p-4 hover:shadow-lg transition-shadow">
    {/* Image */}
    <div className="aspect-square rounded-lg overflow-hidden mb-3 bg-gray-100">
      <img src={post.image_url} alt={post.topic} className="w-full h-full object-cover" />
    </div>
    
    <div className="space-y-2">
      {/* Badge & Engagement */}
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-pink-600 bg-pink-50 px-2 py-0.5 rounded">
          #{index + 1} Trending
        </span>
        <span className="text-xs text-gray-500">{post.engagement}</span>
      </div>
      
      {/* Topic */}
      <h3 className="font-semibold text-gray-800 dark:text-white">{post.topic}</h3>
      
      {/* Caption */}
      <p className="text-sm text-gray-600 line-clamp-2">{post.caption}</p>
      
      {/* Hashtags */}
      <div className="flex flex-wrap gap-1">
        {post.hashtags.map(tag => (
          <span key={tag} className="text-xs text-blue-600">{tag}</span>
        ))}
      </div>
      
      {/* Action Button */}
      <div className="flex gap-2 pt-2">
        <Button 
          size="sm" 
          className="flex-1 bg-gradient-to-r from-pink-500 to-rose-600" 
          onClick={() => onUsePrompt(post)}
          data-testid={`use-trending-${index}`}
        >
          <Sparkles className="h-3 w-3 mr-1" />
          Use This
        </Button>
      </div>
    </div>
  </div>
);

export default TrendingTab;
